/**
 * Excel-Import/Export für Artikel-Stammdaten.
 */

import { API_BASE, ApiError } from "@/lib/apiClient";
import { tenantSlug } from "@/lib/tenantSlug";

export type ArticleImportRowPreview = {
  row_number: number;
  action: "create" | "update" | "error";
  article_number: string;
  name: string;
  category_name: string | null;
  account_number: string | null;
  unit: string | null;
  tax_rate_percent: string | null;
  sale_price_gross: string | null;
  price_valid_from: string | null;
  is_active: boolean | null;
  errors: string[];
  warnings: string[];
};

export type ArticleImportPreview = {
  rows: ArticleImportRowPreview[];
  valid_count: number;
  error_count: number;
  create_count: number;
  update_count: number;
};

export type ArticleImportResult = {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
};

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
}

async function downloadArticleFile(path: string, fallbackFilename: string): Promise<void> {
  const url = API_BASE ? `${API_BASE}${path}` : path;
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: { "X-Tenant-Slug": tenantSlug() },
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : "Download fehlgeschlagen.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const parsed = (await res.json()) as { detail?: unknown };
        if (typeof parsed.detail === "string") message = parsed.detail;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get("Content-Disposition"), fallbackFilename);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export async function downloadArticleImportTemplate(): Promise<void> {
  await downloadArticleFile("/api/v1/articles/export/template", "artikel_vorlage.xlsx");
}

export async function downloadArticleExport(includeInactive = true): Promise<void> {
  const params = new URLSearchParams({ include_inactive: includeInactive ? "true" : "false" });
  await downloadArticleFile(`/api/v1/articles/export?${params}`, "artikel_export.xlsx");
}

async function uploadArticleExcel<T>(path: string, file: File): Promise<T> {
  const url = API_BASE ? `${API_BASE}${path}` : path;
  const form = new FormData();
  form.append("file", file);

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "X-Tenant-Slug": tenantSlug() },
      body: form,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : "Upload fehlgeschlagen.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: unknown };
      if (typeof parsed.detail === "string") message = parsed.detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }

  return (await res.json()) as T;
}

export function previewArticleImport(file: File): Promise<ArticleImportPreview> {
  return uploadArticleExcel<ArticleImportPreview>("/api/v1/articles/import/preview", file);
}

export function applyArticleImport(file: File): Promise<ArticleImportResult> {
  return uploadArticleExcel<ArticleImportResult>("/api/v1/articles/import", file);
}
